import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Plus, FileText, MoreHorizontal, Edit, Trash } from "lucide-react";
import { documentTypeApi } from "@/services/api";
import { DocumentType, isFileDocumentType } from "@/models";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// Define the document type form state with all required fields from DocumentType
interface DocumentTypeFormState {
  name: string;
  source: 'file' | 'platform';
  default_expiration_days?: number;
  product_types: number[];
}

const DocumentTypes = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { hasPermission, getCurrentRoleIds } = useAuth();

  // Check if user can create document types
  const canCreateDocumentType = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'document_types');
  };

  // Fetch document types
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        setIsLoading(true);
        const data = await documentTypeApi.list();
        setDocumentTypes(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err);
        toast({
          title: "Error",
          description: "Failed to load document types. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocumentTypes();
  }, [toast]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // A management-company context sees the shared platform types plus its
  // own; other companies' types stay out of sight (8-Sep).
  const { selectedRole } = useAuth();
  const scopedDocumentTypes = documentTypes.filter((type) => {
    if (selectedRole?.type !== "management_company") return true;
    const mcId = (type as any).management_company_id;
    return mcId == null || Number(mcId) === Number(selectedRole.entityId);
  });

  // Filter document types by search term
  const filteredDocumentTypes = searchTerm
    ? scopedDocumentTypes.filter(type =>
        type.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : scopedDocumentTypes;

  // Handle delete document type
  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this document type?")) {
      try {
        await documentTypeApi.delete(id);
        setDocumentTypes(prev => prev.filter(type => type.id !== id));
        toast({
          title: "Success",
          description: "Document type deleted successfully",
        });
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message || "Failed to delete document type",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Document Types</h1>
            <p className="text-gray-500">
              Manage document type configurations
            </p>
          </div>
          {canCreateDocumentType() && (
            <Button asChild>
              <Link to="/backoffice/document-types/new">
                <Plus className="mr-2 h-4 w-4" />
                Add Document Type
              </Link>
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Document Types</CardTitle>
            <CardDescription>
              Showing {filteredDocumentTypes.length} document types
            </CardDescription>
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search document types..."
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              </div>
            ) : filteredDocumentTypes.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden md:table-cell">Scope</TableHead>
                      <TableHead className="hidden lg:table-cell">Expiration</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocumentTypes.map((documentType) => (
                      <TableRow key={documentType.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center">
                            <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                            <span>{documentType.name}</span>
                          </div>
                        </TableCell>
                        {/* null = platform-wide type; set = one management
                            company's own (8-Sep: "que el document type sea
                            configuración de Management Company"). */}
                        <TableCell className="hidden md:table-cell">
                          <span className="inline-block rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                            {(documentType as any).management_company_id
                              ? `MC #${(documentType as any).management_company_id}`
                              : "Platform"}
                          </span>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {isFileDocumentType(documentType) ? 
                            (documentType.default_expiration_days ? `${documentType.default_expiration_days} days` : 'No expiration') :
                            'Platform (no expiration)'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {/* A MC can use a platform type, not change or
                                  delete it (8-Sep). */}
                              <DropdownMenuItem asChild>
                                <Link to={`/backoffice/document-types/${documentType.id}`}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  {selectedRole?.type === "management_company" &&
                                  !(documentType as any).management_company_id
                                    ? "View"
                                    : "Edit"}
                                </Link>
                              </DropdownMenuItem>
                              {!(
                                selectedRole?.type === "management_company" &&
                                !(documentType as any).management_company_id
                              ) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(documentType.id)}
                                    className="text-red-600"
                                  >
                                    <Trash className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-10">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No document types found</h3>
                <p className="text-sm text-muted-foreground mt-2 mb-6">
                  {searchTerm
                    ? "Try adjusting your search term"
                    : "Get started by creating your first document type"}
                </p>
                <Button asChild>
                  <Link to="/backoffice/document-types/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Document Type
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default DocumentTypes;
